import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronUp, X, Sparkles, ChevronLeft, ChevronRight, Loader2, Utensils, Car, Hotel as HotelIcon, Plane, User } from 'lucide-react';
import { useSuggestedExpenses } from '../../../context/SuggestedExpensesContext';
import { CurrencySelect } from '../../../components/CurrencySelect';
import { useNavigate } from 'react-router-dom';
import { useExpenses } from '../../../context/ExpensesContext';

interface AddFirstExpenseProps {
  tripId: string;
  defaultCategory?: string;
  isProcessing?: boolean;
  onRetryProcessing?: () => void;
}

const PRESET_TAGS = ['Food', 'Transportation', 'Accommodation', 'Flight', 'Supplier'];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Food':
      return <Utensils className="h-3 w-3" />;
    case 'Transportation':
      return <Car className="h-3 w-3" />;
    case 'Accommodation':
      return <HotelIcon className="h-3 w-3" />;
    case 'Flight':
      return <Plane className="h-3 w-3" />;
    case 'Supplier':
      return <User className="h-3 w-3" />;
    default:
      return null;
  }
};

export const AddFirstExpense = ({
  tripId,
  defaultCategory,
  isProcessing = false,
  onRetryProcessing,
}: AddFirstExpenseProps) => {
  const { createExpense } = useExpenses();
  const navigateTo = useNavigate();
  const {
    currentIndex,
    currentExpense,
    totalItems,
    loading: contextLoading,
    navigate: navigateExpenses,
    addExpense,
    addAllExpenses,
  } = useSuggestedExpenses();

  const hasMultipleSuggestions = totalItems > 1;

  // Get current date and time for default description
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const timeStr = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [description, setDescription] = useState(`My ${dateStr} ${timeStr} expense`);
  const [selectedTag, setSelectedTag] = useState<string>(defaultCategory || 'Food');
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [isPrefilled, setIsPrefilled] = useState(false);

  // Default fields
  const [expenseDate, setExpenseDate] = useState(now.toISOString().split('T')[0]);

  // Advanced fields
  const [baseAmount, setBaseAmount] = useState('');
  const [tax, setTax] = useState('');
  const [tip, setTip] = useState('');
  const [fees, setFees] = useState('');
  const [merchant, setMerchant] = useState('');
  const [notes, setNotes] = useState('');
  const [isPlanned, setIsPlanned] = useState(false);

  // Prefill form when current suggested expense changes
  useEffect(() => {
    if (currentExpense) {
      const data = currentExpense.data;
      const fallbackNow = new Date();
      const fallbackDate = fallbackNow.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const fallbackTime = fallbackNow.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      setAmount(data.amount !== undefined && data.amount !== null ? data.amount.toString() : '');
      setCurrency(data.currency || 'USD');
      setDescription(data.description || `My ${fallbackDate} ${fallbackTime} expense`);
      setSelectedTag(data.category || 'Food');
      setExpenseDate(data.expense_date || fallbackNow.toISOString().split('T')[0]);
      setBaseAmount(data.base_amount !== undefined && data.base_amount !== null ? data.base_amount.toString() : '');
      setTax(data.tax !== undefined && data.tax !== null ? data.tax.toString() : '');
      setTip(data.tip !== undefined && data.tip !== null ? data.tip.toString() : '');
      setFees(data.fees !== undefined && data.fees !== null ? data.fees.toString() : '');
      setMerchant(data.merchant || '');
      setNotes('');
      setIsPrefilled(true);
    }
  }, [currentExpense]);

  const loading = contextLoading || manualLoading;

  const allTags = [...PRESET_TAGS, ...customTags];

  const handleAddCustomTag = () => {
    if (newTagInput.trim() && !allTags.includes(newTagInput.trim())) {
      setCustomTags([...customTags, newTagInput.trim()]);
      setNewTagInput('');
    }
  };

  const handleRemoveCustomTag = (tag: string) => {
    setCustomTags(customTags.filter(t => t !== tag));
    if (selectedTag === tag) {
      setSelectedTag('Food');
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) < 0) return;

    if (currentExpense) {
      // Adding a suggested expense with (possibly edited) form data
      await addExpense(currentExpense.id, {
        tripId,
        category: selectedTag,
        description,
        amount: parseFloat(amount),
        currency,
        expense_date: expenseDate,
        base_amount: baseAmount ? parseFloat(baseAmount) : undefined,
        tax: tax ? parseFloat(tax) : undefined,
        tip: tip ? parseFloat(tip) : undefined,
        fees: fees ? parseFloat(fees) : undefined,
        merchant: merchant || undefined,
        notes: notes || undefined,
        is_planned: isPlanned,
        receipt_url: currentExpense.receiptUrl,
        original_file_name: currentExpense.originalFileName,
      });
    } else {
      // Manual expense creation (no suggestions)
      setManualLoading(true);
      try {
        await createExpense({
          trip_id: tripId,
          category: selectedTag,
          description,
          amount: parseFloat(amount),
          base_amount: baseAmount ? parseFloat(baseAmount) : undefined,
          tax: tax ? parseFloat(tax) : undefined,
          tip: tip ? parseFloat(tip) : undefined,
          fees: fees ? parseFloat(fees) : undefined,
          currency,
          expense_date: expenseDate,
          is_planned: isPlanned,
          merchant: merchant || undefined,
          notes: notes || undefined,
        });
        navigateTo(`/trip/${tripId}`);
      } catch (error) {
        console.error('Error creating expense:', error);
      } finally {
        setManualLoading(false);
      }
    }
  };

  // Show loading state when processing receipt
  if (isProcessing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Add Expense</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-lg font-medium">Processing receipt...</p>
            <p className="text-sm text-muted-foreground mt-2">Extracting expense data with AI</p>
          </div>
          {onRetryProcessing && (
            <Button
              onClick={onRetryProcessing}
              variant="outline"
              className="mt-4"
            >
              Retry with OpenAI
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const isCurrentAdded = currentExpense?.isAdded ?? false;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Add Expense</CardTitle>
          {isPrefilled && (
            <Badge variant="secondary" className="gap-1">
              <Sparkles className="h-3 w-3" />
              AI Extracted
            </Badge>
          )}
        </div>
        {isPrefilled && (
          <p className="text-sm text-muted-foreground mt-2">
            Review and edit the details below, then save to create your expense.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Amount and Currency */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="How much did I spend? 🤔"
              className="text-2xl font-bold h-14"
            />
          </div>
          <div className="w-36">
            <Label htmlFor="currency">Currency</Label>
            <CurrencySelect
              value={currency}
              onValueChange={setCurrency}
              className="h-14"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`My ${dateStr} ${timeStr} expense`}
            className="text-lg"
          />
        </div>

        {/* Expense Date */}
        <div>
          <Label htmlFor="expense-date">Expense Date</Label>
          <Input
            id="expense-date"
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            className="text-lg"
          />
        </div>

        {/* Tags */}
        <div>
          <Label>Category</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {allTags.map((tag) => (
              <div key={tag} className="relative">
                <Badge
                  variant={selectedTag === tag ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm py-2 px-3 ${customTags.includes(tag) ? 'pr-8' : ''} inline-flex items-center justify-center gap-1.5`}
                  onClick={() => setSelectedTag(tag)}
                >
                  {getCategoryIcon(tag)}
                  {tag}
                </Badge>
                {customTags.includes(tag) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustomTag(tag);
                    }}
                    className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            <Input
              value={newTagInput}
              onChange={(e) => setNewTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomTag();
                }
              }}
              placeholder="+ New tag"
              className="w-32 h-10 rounded-full px-4 text-center text-sm"
            />
          </div>
        </div>

        {/* Advanced Toggle */}
        <Button
          onClick={() => setShowAdvanced(!showAdvanced)}
          variant="ghost"
          className="w-full"
        >
          {showAdvanced ? (
            <>
              <ChevronUp className="mr-2 h-4 w-4" />
              Hide Advanced
            </>
          ) : (
            <>
              <ChevronDown className="mr-2 h-4 w-4" />
              Advanced
            </>
          )}
        </Button>

        {/* Advanced Fields */}
        {showAdvanced && (
          <div className="space-y-4 pt-4 border-t">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="base-amount">Base Amount</Label>
                <Input
                  id="base-amount"
                  type="number"
                  value={baseAmount}
                  onChange={(e) => setBaseAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="tax">Tax</Label>
                <Input
                  id="tax"
                  type="number"
                  value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="tip">Tip</Label>
                <Input
                  id="tip"
                  type="number"
                  value={tip}
                  onChange={(e) => setTip(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="fees">Fees</Label>
                <Input
                  id="fees"
                  type="number"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="merchant">Merchant</Label>
              <Input
                id="merchant"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                placeholder="Store or vendor name..."
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is-planned" className="cursor-pointer">
                {isPlanned ? 'Planned' : 'Unplanned'}
              </Label>
              <Switch
                id="is-planned"
                checked={isPlanned}
                onCheckedChange={setIsPlanned}
              />
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        {isCurrentAdded ? (
          <Button
            disabled
            className="w-full h-12 text-lg font-semibold"
            variant="outline"
          >
            Already Added ✓
          </Button>
        ) : hasMultipleSuggestions ? (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleSubmit}
              disabled={loading || !amount || parseFloat(amount) < 0}
              className="w-full sm:w-1/2 h-12 text-lg font-semibold"
            >
              {loading ? 'Adding...' : 'Add Expense'}
            </Button>
            <Button
              onClick={addAllExpenses}
              disabled={loading}
              variant="outline"
              className="w-full sm:w-1/2 h-12 text-lg font-semibold"
            >
              {`Add All ${totalItems} Expenses`}
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading || !amount || parseFloat(amount) < 0}
            className="w-full h-12 text-lg font-semibold"
          >
            {loading ? 'Adding...' : 'Add Expense'}
          </Button>
        )}

        {/* Navigation Controls */}
        {hasMultipleSuggestions && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateExpenses('prev')}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[3rem] text-center">
              {currentIndex + 1} / {totalItems}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateExpenses('next')}
              disabled={currentIndex === totalItems - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
